import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Icons,
  iconSize,
  Link,
  palette,
  Stack,
  StatusChip,
  Typography,
} from '@munifocus/ui';
import type { FormKind, FormSummary } from '@munifocus/contracts-gov';
import {
  downloadDraft,
  useCreateDraft,
  usePublishForm,
  useReviseForm,
  useUploadDraft,
} from '../lib/forms-api';
import { formatDateTime } from '../lib/relative-time';
import { DraftEditor } from './DraftEditor';
import { SignoffPanel } from './signoff/SignoffPanel';

/**
 * The authoring lifecycle for one document kind: draft → publish → supersede.
 *
 * Editing happens in the officer's own Word or LibreOffice — they download the
 * draft, edit it, and upload it back. That is how these documents are actually
 * written today (freely, with the lawyer's feedback coming afterwards), and it
 * is the only editing surface that cannot degrade the layout, because nothing
 * re-renders the document on the way through. What the portal adds is the part
 * that was missing: a working copy that carries no legal weight, a moment at
 * which it becomes operative, and a version history that survives being
 * replaced.
 */
export function DocumentLifecycle({
  fileId,
  kind,
  forms,
}: {
  fileId: string;
  kind: FormKind;
  /** Every version of this kind on the file, newest first. */
  forms: FormSummary[];
}) {
  const { t, i18n } = useTranslation();
  const uploadInput = useRef<HTMLInputElement>(null);

  const createDraft = useCreateDraft(fileId);
  const uploadEdited = useUploadDraft(fileId);
  const publish = usePublishForm(fileId);
  const revise = useReviseForm(fileId);
  const [editing, setEditing] = useState(false);

  const draft = forms.find((f) => f.status === 'draft');
  const published = forms.find((f) => f.status === 'published');
  const superseded = forms.filter((f) => f.status === 'superseded');

  const name = t(`gov.forms.kind.${kind}`);
  const busy =
    createDraft.isPending || uploadEdited.isPending || publish.isPending || revise.isPending;

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset first: picking the same filename twice must still fire a change.
    e.target.value = '';
    if (file && draft) {
      uploadEdited.mutate({ formId: draft.id, file, expectedUpdatedAt: draft.updatedAt });
    }
  };

  return (
    <Stack spacing={1}>
      {draft && (
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <StatusChip
              status="pending"
              label={t('gov.forms.statusDraft', { version: draft.version })}
            />
            <Typography variant="caption" color="text.secondary">
              {t('gov.forms.draftHint')}
            </Typography>
          </Stack>

          <Stack useFlexGap direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {/* First, because it is the shorter road for the common edit —
                changing what the document says. Word stays for what this
                cannot express: inserting a paragraph, changing a table,
                restyling. */}
            <Button
              size="small"
              variant="contained"
              startIcon={<Icons.Pencil size={iconSize.md} />}
              disabled={busy}
              onClick={() => setEditing(true)}
            >
              {t('gov.forms.editHere')}
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<Icons.Download size={iconSize.md} />}
              disabled={busy}
              onClick={() => downloadDraft(fileId, draft.id, `${name}.docx`)}
            >
              {t('gov.forms.editInWord')}
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<Icons.Upload size={iconSize.md} />}
              disabled={busy}
              onClick={() => uploadInput.current?.click()}
            >
              {uploadEdited.isPending ? t('gov.forms.uploading') : t('gov.forms.uploadEdited')}
            </Button>
            <input
              ref={uploadInput}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              hidden
              onChange={onPickFile}
            />

            <Button
              size="small"
              variant="contained"
              startIcon={<Icons.BadgeCheck size={iconSize.md} />}
              disabled={busy}
              onClick={() => publish.mutate({ formId: draft.id })}
            >
              {publish.isPending ? t('gov.forms.publishing') : t('gov.forms.publish')}
            </Button>
          </Stack>
        </Stack>
      )}

      {/* The published copy's own download lives in the catalog's format list
          just below; repeating it here would give the officer two links to the
          same file. What this adds is the state and who froze it. */}
      {!draft && published && (
        <Stack spacing={0.75}>
          <Stack
            useFlexGap
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <StatusChip
              status="success"
              label={t('gov.forms.statusPublished', { version: published.version })}
            />
            {published.publishedByName && (
              <Typography variant="caption" color="text.secondary">
                {t('gov.forms.publishedBy', {
                  name: published.publishedByName,
                  at: formatDateTime(published.publishedAt ?? published.generatedAt, i18n.language),
                })}
              </Typography>
            )}
          </Stack>

          {/* Review and e-signature, where the municipality has turned it on. */}
          <SignoffPanel fileId={fileId} published={published} />

          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icons.RefreshCw size={iconSize.md} />}
              disabled={busy}
              onClick={() => revise.mutate({ formId: published.id })}
            >
              {revise.isPending ? t('gov.forms.revising') : t('gov.forms.revise')}
            </Button>
          </Box>
        </Stack>
      )}

      {!draft && !published && (
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icons.Pencil size={iconSize.md} />}
            disabled={busy}
            onClick={() => createDraft.mutate({ kind })}
          >
            {createDraft.isPending ? t('gov.forms.startingDraft') : t('gov.forms.startDraft')}
          </Button>
        </Box>
      )}

      {superseded.length > 0 && (
        <Stack spacing={0.25}>
          <Typography variant="caption" sx={{ color: palette.text.muted }}>
            {t('gov.forms.earlierVersions')}
          </Typography>
          {superseded.map((old) => (
            <Stack key={old.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <StatusChip
                status="neutral"
                label={t('gov.forms.statusSuperseded', { version: old.version })}
              />
              {old.url && (
                <Link
                  href={old.url}
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  aria-label={t('gov.forms.downloadVersionAria', {
                    name,
                    version: old.version,
                  })}
                >
                  <Typography variant="caption">
                    {formatDateTime(old.publishedAt ?? old.generatedAt, i18n.language)}
                  </Typography>
                </Link>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {/* Mounted only while a draft exists — there is nothing to author once a
          document is published, and its runs are no longer editable. */}
      {draft && (
        <DraftEditor
          fileId={fileId}
          formId={draft.id}
          name={name}
          open={editing}
          onClose={() => setEditing(false)}
        />
      )}
    </Stack>
  );
}
